<template>
  <list-layout 
    :title="(isSubTab ? '' : $t('payouts')) | startcase"
    :search-filter="!isSubTab ? filters.search : null"
    :apply-filters="!isSubTab ? applyFilters: null"
    no-counts
    no-search
    no-apply-btn
    >

    <template v-slot:heading-right>
      <b-button
        v-if="permissions.pages[Pages.PayoutsList].canWrite && !recordingResoundId && !rightsholderResoundId"
        class="ml-4"
        variant="secondary"
        :to="{'name': 'payout-create'}"
      >{{ $t("create_new_payout") | startcase }}</b-button>
    </template>

    <template v-slot:beforeFilters>
      <b-row>
        <b-col>
          <filter-builder
            ref="payoutsFilters"
            :id="userPreferenceNames.PAYOUT_LIST_FILTER"
            :filters="getFiltersWithOptions()"
            :apply-filters="applyFilters"
            :reset-filters="resetFilters"
            :filters-pending="filtersPending"
            :filters-applied="filtersApplied"
            @sorted="onSorted"
            @filter="onFilter"
          />
          
        </b-col>
      </b-row>
      <b-row class="text-center mb-3" align-v="center" align-h="center">
        <b-col>
          <b-form-radio-group
            v-if="isSubTab && isTransactionsSubTab"
            v-model="localCurrentTab"
            :options="transactionsOptions"
            buttons
            button-variant="outline-secondary"
            name="radios-btn-outline"
            class="radio-buttons mr-3"
          />
          <b-form-radio-group
            v-model="payoutsTable.selectedStatus"
            :options="filteredPayoutStatus"
            buttons
            button-variant="outline-secondary"
            name="radios-btn-outline"
            class="radio-buttons"
          />
        </b-col>
      </b-row>
    </template>

    <template v-slot:table>
      <t-table
        :columns="columns"
        :options="options"
        :limit="limit"
        ref="payoutlist"
        id="payouts-list-table"
        @loaded="onLoaded"
        @sorted="onSorted"
        @pagination="onPagination"
        @loading="onLoading"
      >
        <template v-slot:counts>
          <div class="d-flex">
            <pagination
              class="smaller p-0"
              :page="page"
              :count="count"
              :loading="loading"
              v-model="limit"
              @limit="onLimit"
            />
          </div>
        </template>

        <!-- Created ID -->
        <template v-slot:createdBy="{ row }">
          {{ row.createdByName }}
        </template>

        <!-- Batch ID -->
        <template v-slot:transactionBatchId="{ row }">
          <span v-if="isSubTab" class="routerLink" @click="viewTransaction('transactionTransactionBatchId', row?.transactionBatchId, row?.transactionBatchId)">
            {{ row.transactionBatchId }}
          </span>
          <span v-else>
            <database-link :type="linkTypes.PAYOUT" :item-id="row.id">{{ row.transactionBatchId }}</database-link>
          </span>

        </template>

        <template v-slot:payoutId="{ row }">
          <span class="routerLink" v-if="isSubTab" @click="viewTransaction('transactionPayoutCompletedId', row?.payoutId, row?.payoutId)">
            {{ row.payoutId }}
          </span>
          <span v-else>
            <database-link :type="linkTypes.PAYOUT" :item-id="row.id">{{ row.payoutId }}</database-link>
          </span>
        </template>

        <!-- Tariffs Included--> 
        <template v-slot:distributionIds="{ row }">
          <NamesListModal
              v-if="row.status !== payoutStatuses.NOT_STARTED"
              class-names="table-meta mb-2"
              :values="mapTariffs(row.distributionsMap)"
              modalTitle="tariff periods"
              :text-plural="$t('tariff_periods')"
              :text-exception="$t('all_except') | capitalize"
              :text-none="$t('all') | capitalize"
              :exception="row.scope?.distributions?.select === 'exclude'"
          />
        </template>

      <!-- Status -->
        <template v-slot:status="{ row }">
          <status
              v-if="row.status === payoutStatuses.IN_REVIEW"
              :text="capitalize($t('in_review'))"
              name="in_review"
              :icon="['fas', 'question-circle']"
              color="orange"
            />
            <status
              v-else-if="row.status === payoutStatuses.NOT_STARTED"
              :text="capitalize($t('not_started'))"
              name="not_started"
              :icon="['fas', 'question-circle']"
              color="orange"
            />
            <status
              v-else-if="row.status === payoutStatuses.SCOPED"
              :text="capitalize($t('scoped'))"
              name="scoped"
              :icon="['fas', 'question-circle']"
              color="orange"
            />
            <status
              v-else-if="row.status === payoutStatuses.APPROVED"
              :text="capitalize($t('approved'))"
              name="approved"
              :icon="['fas', 'question-circle']"
              color="orange"
            />
            <status
              v-else-if="row.status === payoutStatuses.REPORT_GENERATED"
              :text="capitalize($t('report_generated'))"
              name="report_generated"
              :icon="['fas', 'question-circle']"
              color="orange"
            />
            <status
              v-else-if="row.status === payoutStatuses.COMPLETED"
              :text="capitalize($t('statement_generated'))"
              name="statement_generated"
              :icon="['fas', 'check-circle']"
              color="green"
            />
            <status
              v-else-if="row.status === payoutStatuses.CANCELLED"
              name="cancelled"
              :text="capitalize($t('cancelled'))"
              :icon="['fas', 'exclamation-circle']"
              color="red"
            />
            <status
              v-else-if="row.status === payoutStatuses.ERROR"
              name="error"
              :icon="['fas', 'exclamation-circle']"
              color="red"
              :text="capitalize($t('error'))"
              @click="showError()"
              class="error-status"
            />
        </template>
        <!-- Action -->
        <template v-slot:action="{ row }">
          <span v-show="isInProgress(row.status) && !loading">
            <fa-icon
              v-if="permissions.pages[Pages.PayoutsList].canWrite"
              role="button"
              size="1x"
              :title="$t('delete') | lowercase"
              :icon="['fa', 'trash']"
              class="cursor-pointer"
              color="#c23d5b"
              @click.stop="onDelete(row)"
            />
          </span>
        </template>
      </t-table>
    </template>
    <!-- Deletion Confirmation Modal -->
    <modal
      :id="'confirmation-modal'"
      :customTitle="$t('cancel_payout') | capitalize"
      modal-class="custom-modal"
      modalType="info"
      size="md"
      centered
      @ok="onConfirmDelete()"
      okTitle="Confirm"
    >
      <div>
        {{ `You are about to cancel the payout ${this.modalPayoutData?.transactionBatchId} and all its associated transactions. This action is irreversible and will permanently remove the data.` }} <br>
        <br>
        {{ "Are you sure you want to proceed?" }}
      </div>
    </modal>
    <!-- Error modal -->
    <modal
      id="payoutErrorsModal"
      ref="payoutErrorsModal"
      :customTitle="$t('error') | capitalize"
      modal-class="custom-modal error"
      modalType="error"
      size="l"
      centered
      hide-footer >
        {{ this.$t("payout_failed") }}
    </modal>
  </list-layout>
</template>

<script>
import { capitalize, get, snakeCase } from "lodash"
import { 
  filterTypes, 
  linkTypes, 
  payoutStatuses, 
  perPageItemsLg, 
  userPreferenceNames 
} from "@/constants"
import { mapMutations, mapState } from "vuex"
import DatabaseLink from "@/components/DatabaseLink.vue"
import FilterBuilder from "@/components/FilterBuilder"
import NamesListModal from "@/pages/Repertoire/Recordings/NamesListModal"
import { Pages } from "@/utils/pages"
import Pagination from "@/components/Pagination"
import { listRouteMixin } from "@/utils/mixins"
import parseDate from "@/utils/date-parser"

export default {
  name: "ListPayoutsPage",
  mixins: [listRouteMixin],
  components: { 
    FilterBuilder,
    Pagination,
    DatabaseLink,
    NamesListModal
  },
  props: {
    isSubTab: {
      type: Boolean,
      default: false,
    },
    isTransactionsSubTab: {
      type: Boolean,
      default: false,
    },
    distributionId: {
      type: String,
      required: false,
    },
    recordingResoundId: {
      type: String,
      required: false,
    },
    rightsholderResoundId: {
      type: String,
      required: false,
    },
    transactionsOptions: Array,
    currentTab: String,
  },
  data () {
    return {
      page: 1,
      count: 0,
      limit: perPageItemsLg,
      loading: false,
      userPreferenceNames,
      capitalize,
      get,
      Pages,
      linkTypes,
      payoutStatuses,
      modalPayoutData: {},
      distributionsIdsOptions: [],
      payoutsTable: {
        status: [
          {
            value: "all",
            text: capitalize(this.$t("all")),
          },
          {
            value: payoutStatuses.IN_PROGRESS,
            text: capitalize(this.$t("in_progress")),
          },
          {
            value: payoutStatuses.COMPLETED,
            text: capitalize(this.$t("completed")),
          },
          {
            value: payoutStatuses.CANCELLED,
            text: capitalize(this.$t("cancelled")),
          },
        ],
        selectedStatus: null,
      },
      filters: {
        search: { value: "", defaultValue: "", type: filterTypes.TEXT, label: capitalize(this.$t("search")) },
        payoutPayoutId: { value: null, defaultValue: null, type: filterTypes.TEXT, label: capitalize(this.$t("payout_ID")), hidden: false },
        payoutTransactionBatchId: { value: null, defaultValue: null, type: filterTypes.TEXT, label: capitalize(this.$t("batch_id")), hidden: false },
        status: { 
          value: null, 
          defaultValue: null, 
          type: filterTypes.SELECT, 
          label: capitalize(this.$t("status")),
          options: [], 
        },
        createdBy: {
          value: null,
          defaultValue: null,
          type: filterTypes.SELECT_SEARCH,
          listEndpoint: this.$api.users.usersSearch,
          excludeName: "id",
          textField: "name",
          valueField: "email",
          label: capitalize(this.$t("created_by")),
          placeholder: capitalize(this.$t("search_user")),
          searchName: "search",
          noWatch: true,
        },
        managedBy: {
          value: null,
          defaultValue: null,
          type: filterTypes.SELECT_SEARCH,
          listEndpoint: this.$api.users.usersSearch,
          excludeName: "id",
          textField: "name",
          valueField: "email",
          label: capitalize(this.$t("managed_by")),
          placeholder: capitalize(this.$t("search_user")),
          searchName: "search",
          noWatch: true,
        },
        description: { value: "", defaultValue: "", type: filterTypes.TEXT, label: capitalize(this.$t("description")) },
        distributionsCount: { value: "", defaultValue: null, type: filterTypes.TEXT, label: capitalize(this.$t("tariffs_in_scope")) },
        distributionIds: {
          value: "",
          defaultValue: "",
          placeholder: capitalize(this.$t("search_tariff_period")),
          type: filterTypes.MULTI_SELECT_SEARCH,
          label: capitalize(this.$t("tariffs_included")),
          listEndpoint: this.findData,
          options: [],
          showNoResults: true,
          isLoading: false,
          noWatch: true,
        },
        estimatedCompletionDate: {
          value: null,
          defaultValue: null,
          type: filterTypes.DATE_RANGE,
          format: "YYYY-MM-DD",
          label: capitalize(this.$t("estimated_completion_date")),
          noWatch: true,
          hidden: false,
        },
        completionDate: {
          value: null,
          defaultValue: null,
          type: filterTypes.DATE_RANGE,
          format: "YYYY-MM-DD",
          label: capitalize(this.$t("actual_completion_date")),
          noWatch: true,
          hidden: false,
        },
        rightsHolderProprietaryId: {
          value: null,
          defaultValue: null,
          type: filterTypes.SELECT_SEARCH,
          label: capitalize(this.$t("rights_holder")),
          placeholder: capitalize(this.$t("search_rightsholder")),
          excludeName: "id",
          valueField: "resoundId",
          textField: "name",
          searchName: "search",
          listEndpoint: this.$api.rightsholders.rightsholdersList,
          noWatch: true,
          visible: false,
          hidden: true
        },
        recordingProprietaryId: {
          value: null,
          defaultValue: null,
          type: filterTypes.SELECT_SEARCH,
          label: capitalize(this.$t("recording")),
          placeholder: capitalize(this.$t("search_recording")),
          excludeName: "id",
          valueField: "resoundId",
          textFunction: (r => `${r.artist} - ${r.title}`),
          searchName: "fuzzy_search",
          listEndpoint: this.$api.repertoire.recordingsList,
          noWatch: true,
          visible: false,
          hidden: true
        },
        userTimezone: { value: null, defaultValue: null, hidden: true },
      },
      options: {
        sortable: [
          "transactionBatchId",
          "payoutId",
          "createdBy",
          "managedBy",
          "description",
          "estimatedCompletionDate",
          "completionDate",
          "distributionsCount",
          "distributionIds",
          "status",
          "action"
        ],
        headings: {
          transactionBatchId: this.$t("batch_id"),
          payoutId: this.$t("payout_ID"),
          createdBy: capitalize(this.$t("created_by")),
          managedBy: capitalize(this.$t("managed_by")),
          description: capitalize(this.$t("description")),
          estimatedCompletionDate: capitalize(this.$t("estimated_completion_date")),
          completionDate: capitalize(this.$t("actual_completion_date")),
          distributionsCount: capitalize(this.$t("tariffs_in_scope")),
          distributionIds: capitalize(this.$t("tariffs_included")),
          status: capitalize(this.$t("status")),
          action: ""
        },
        width: {
          transactionBatchId: 100,
          payoutId: 100,
          createdBy: 150,
          managedBy: 180,
          description: 150,
          status: 120,
          action: 130,
        },
        responseAdapter ({ data }) {
          return {
            data: data.results,
            count: data.count,
          }
        },
        requestFunction () {
          let queryParams = { ...this.$route.query }
          this.page = Number(get(queryParams, "page", 1))

          if (queryParams.payout_payout_id) {
            queryParams.payout_id = queryParams.payout_payout_id
            delete queryParams.payout_payout_id
          }
          if (queryParams.payout_transaction_batch_id) {
            queryParams.transaction_batch_id = queryParams.payout_transaction_batch_id
            delete queryParams.payout_transaction_batch_id
          }

          return this.$api.payouts.payoutsList(queryParams)
        },
      }
    }
  },
  methods: {
    ...mapMutations("alert", ["success", "error"]),
    parseDate,
    onDelete (rowData) {
      this.modalPayoutData = rowData
      this.$bvModal.show("confirmation-modal")
    },
    onConfirmDelete () {
      this.$api.payouts.deletePayout(this.modalPayoutData.id)
        .then(() => {
          let successMsg = `Payout ${this.modalPayoutData.transactionBatchId} and all its associated transactions have been successfully cancelled. This action cannot be undone.`
          this.success(successMsg)
          this.getData()
          this.modalPayoutData = {}
        })
        .catch((error) => {
          if (error.response.status === 400) {
            let msg = `Error deleting payout. ${error.response.data.nonFieldErrors[0].message}`
            this.error(msg)
          }
        })
    },
    viewTransaction (filterName, id) {
      this.localCurrentTab = "transactions_tab"
      this.$emit("applyPayoutFilter", filterName, id)
    },
    onLoaded ({ data }) {
      this.count = data.count
      this.summary = data.summary
      this.loading = false
    },
    getData () {
      this.$refs.payoutlist.getData()
    },
    onSorted (data) {
      this.queryParams = {
        ...this.queryParams,
        ["sorting"]: data.map(item => item.ascending ? toSortParam(item.column) : `-${toSortParam(item.column)}`)
      }
      this.applyFilters()
    },
    onFilter (name,value) {
      if (name === "distributionIds") {
        if (!value) this.distributionsIdsOptions = []
        this.changeFilter("distribution_ids", value)
      } else if (name === "createdBy" || name === "managedBy") {
        if (value === null) {
          this.changeFilter(name, value)
        } else {
          this.changeFilter(name, value.value)
        }
      } else if (["estimatedCompletionDate", "completionDate"].includes(name)) {
        const range = (value) ?
          [
            this.parseDate(value[0], this.$config.ISO_DATE_FORMAT),
            this.parseDate(value[1], this.$config.ISO_DATE_FORMAT),
          ] : []
        const hasValidDates = range.some(date => !!date)
        if (hasValidDates) {
          this.changeFilter("userTimezone", Intl.DateTimeFormat().resolvedOptions().timeZone)
          this.changeFilter(name, range)
        } else {
          this.changeFilter("userTimezone", null)
          this.changeFilter(name, null)
        }
      }
    },
    getFiltersWithOptions () {
      if (this.payoutsTable.selectedStatus === payoutStatuses.IN_PROGRESS) {
        this.filters.status.options = this.payoutStatusOptions.filter(option => 
          option.value !== payoutStatuses.COMPLETED && option.value !== payoutStatuses.CANCELLED
        )
      } else {
        this.filters.status.options = this.payoutStatusOptions.filter(
          option => option.value !== payoutStatuses.CANCELLED
        )
      }
      this.filters.payoutPayoutId.hidden = this.payoutsTable.selectedStatus === payoutStatuses.IN_PROGRESS || this.payoutsTable.selectedStatus === payoutStatuses.CANCELLED
      this.filters.payoutTransactionBatchId.hidden = this.payoutsTable.selectedStatus === payoutStatuses.COMPLETED
      this.filters.status.hidden = this.payoutsTable.selectedStatus === payoutStatuses.CANCELLED || this.payoutsTable.selectedStatus === payoutStatuses.COMPLETED
      return this.filters
    },
    mapTariffs (tariffMap) {
      return tariffMap ? Object.values(tariffMap).map(item => item.description) : []
    },
    isInProgress (status) {
      const inProgressStatusList = [payoutStatuses.IN_REVIEW, payoutStatuses.NOT_STARTED, payoutStatuses.SCOPED, payoutStatuses.APPROVED, payoutStatuses.REPORT_GENERATED]
      return inProgressStatusList.includes(status)
    },
    showError () {
      this.$bvModal.show("payoutErrorsModal")
    },
    findData (query) {
      if (query) {
        this.filters.distributionIds.isLoading = true
        this.$api.distributions.distributionsList({ description: query }).then((response) => {
          this.distributionsIdsOptions = response.data.results
            .map(r => {
              return {
                "name": r.description,
                "value": r.id,
              }
            })
        }).finally(() => {
          this.filters.distributionIds.isLoading = false
        })
      }
    },
  },
  computed: {
    ...mapState("user", ["user", "permissions"]),
    columns () {
      let cols = [
        "transactionBatchId",
        "payoutId",
        "createdBy",
        "managedBy",
        "description",
        "estimatedCompletionDate",
        "completionDate",
        "distributionsCount",
        "distributionIds",
        "status",
        "action",
      ]
      if (this.payoutsTable.selectedStatus === payoutStatuses.IN_PROGRESS || this.payoutsTable.selectedStatus === payoutStatuses.CANCELLED) {
        cols = cols.filter(col => col !== "payoutId")
      } else if (this.payoutsTable.selectedStatus === payoutStatuses.COMPLETED) {
        cols = cols.filter(col => col !== "transactionBatchId")
      }
      return cols
    },
    localCurrentTab: {
      get () {
        return this.currentTab
      },
      set (value) {
        this.$emit("update:currentTab", value)
      }
    },
    filteredPayoutStatus () {
      if (this.rightsholderResoundId || this.recordingResoundId) {
        return this.payoutsTable.status.filter(option => option.value === payoutStatuses.COMPLETED)
      }
      return this.payoutsTable.status.filter(option => {
        return !(option.value === payoutStatuses.CANCELLED && !this.permissions.actions.canReadCancelledPayouts)
      })
    },
    payoutStatusOptions () {
      let permittedPayoutStatuses = [
        {
          value: payoutStatuses.IN_REVIEW,
          text: capitalize(this.$t("in_review")),
        },
        {
          value: payoutStatuses.REPORT_GENERATED,
          text: capitalize(this.$t("report_generated")),
        },
        {
          value: payoutStatuses.APPROVED,
          text: capitalize(this.$t("approved")),
        },
        {
          value: payoutStatuses.COMPLETED,
          text: capitalize(this.$t("completed")),
        },
      ]
      if (this.permissions.actions.canReadNotStartedScopedPayouts) {
        permittedPayoutStatuses.unshift({
          value: payoutStatuses.NOT_STARTED,
          text: capitalize(this.$t("not_started")),
        },
        {
          value: payoutStatuses.SCOPED,
          text: capitalize(this.$t("scoped")),
        })
      }
      if (this.permissions.actions.canReadCancelledPayouts) {
        permittedPayoutStatuses.push({
          value: payoutStatuses.CANCELLED,
          text: capitalize(this.$t("cancelled")),
        })
      }
      return permittedPayoutStatuses
    }
  },
  watch: {
    "payoutsTable.selectedStatus": function (newVal) {
      this.filters.status.value = newVal
      this.queryParams = {
        ...this.queryParams, "status": newVal
      }
      delete this.queryParams["sorting"]
      this.applyFilters()
    },
    "filters.status.value": function (newVal) {
      if (!newVal) {
        this.filters.status.value = this.payoutsTable.selectedStatus
        this.queryParams = {
          ...this.queryParams, "status": this.payoutsTable.selectedStatus
        }
        this.applyFilters()
      }
    },
    distributionsIdsOptions () {
      this.filters.distributionIds.options = this.distributionsIdsOptions
    }
  },
  mounted () {
    this.updateTableSortIcons(this.$refs.payoutlist.$children[0], true)
    const currentStatus = this.$route.query["status"]
    if (this.recordingResoundId || this.rightsholderResoundId) {
      this.payoutsTable.selectedStatus = payoutStatuses.COMPLETED
    } else if (currentStatus === payoutStatuses.IN_PROGRESS || currentStatus === undefined) {
      this.payoutsTable.selectedStatus = payoutStatuses.IN_PROGRESS
    } else {
      this.payoutsTable.selectedStatus = "all"
    }
    if (this.distributionId) {
      this.changeFilter("distribution_id", this.distributionId)
      this.filters.distributionIds.hidden = true
    }
    if (this.recordingResoundId) {
      this.changeFilter("recordingProprietaryId", this.recordingProprietaryId)
    }
    if (this.rightsholderResoundId) {
      this.changeFilter("rightsHolderProprietaryId", this.rightsHolderProprietaryId)
    }

    const filtersFromQuery = Object.keys(this.$route.query).filter(
      key => !["sorting", "aggregate_by", "page", "limit"].includes(key)
    )

    // In case when page is opened with some filters applied (through shared link for example)
    if (filtersFromQuery.length > 0) {
      this.firstLoaded = true
      filtersFromQuery.forEach(filter => {
        if (
          filter === "recording_proprietary_id" ||
          filter === "rights_holder_proprietary_id"
        ) {
          this.changeFilter(filter, this.$route.query[filter])
          this.filters[filter].searchValue = this.$route.query[filter]
        }
      })
    }
  },
}

function toSortParam (param) {
  return snakeCase(param)
}
</script>

<style lang="scss" scoped>
.routerLink {
  cursor: pointer;
  color: var(--info);
}
::v-deep .no-title {
  padding-top: 0 !important;
}
.error-status {
  cursor: pointer;
}
</style>